import React from 'react';
import { Target, TrendingUp, Globe, Shield } from 'lucide-react';

export const About = () => {
  const values = [
    {
      icon: <Target className="w-8 h-8 text-orange-600" />,
      title: "Innovation First",
      description: "We push boundaries and embrace cutting-edge blockchain technology to solve Africa's unique challenges."
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-orange-600" />,
      title: "Impact Driven",
      description: "Every venture we build is designed to create meaningful impact and drive financial inclusion across the continent."
    },
    {
      icon: <Globe className="w-8 h-8 text-orange-600" />,
      title: "Pan-African Vision",
      description: "We're building for all of Africa, connecting markets and creating opportunities across borders."
    },
    {
      icon: <Shield className="w-8 h-8 text-orange-600" />,
      title: "Trust & Transparency",
      description: "Blockchain's core values guide our work—we build with integrity and transparency at the forefront."
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="px-6 mb-20">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-slate-900 mb-6">About Kesho Studio</h1>
          <p className="text-xl text-slate-600 leading-relaxed">
            Kesho Studio is a pioneering crypto venture builder and investor dedicated to accelerating blockchain innovation across Africa. We identify, build, and scale transformative Web3 ventures that address real-world challenges and unlock opportunities for millions.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="px-6 py-16 bg-slate-50 mb-20">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Mission</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-4">
                To be Africa's leading Web3 venture studio, building the infrastructure and applications that will power the continent's digital economy.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                We believe blockchain technology is the key to unlocking financial inclusion, transparency, and economic opportunity across Africa. Through strategic investments and hands-on venture building, we're making this vision a reality.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">What We Do</h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start">
                  <span className="text-orange-600 font-bold mr-2">•</span>
                  <span>Build and launch innovative Web3 ventures from the ground up</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 font-bold mr-2">•</span>
                  <span>Invest in promising blockchain startups across Africa</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 font-bold mr-2">•</span>
                  <span>Provide technical expertise and strategic guidance</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 font-bold mr-2">•</span>
                  <span>Connect ventures with funding and partnership opportunities</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 font-bold mr-2">•</span>
                  <span>Drive crypto adoption through education and advocacy</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="px-6 mb-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-white border border-slate-200 hover:shadow-lg transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-50 rounded-full mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="px-6 py-16 bg-orange-600 text-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Our Approach</h2>
          <p className="text-xl text-orange-100 leading-relaxed mb-8">
            We don't just invest—we build. Our hands-on approach combines deep technical expertise with strategic business acumen. From ideation to launch and beyond, we work alongside our portfolio companies to ensure success.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div>
              <h3 className="text-6xl font-bold mb-2">01</h3>
              <h4 className="text-xl font-semibold mb-2">Identify</h4>
              <p className="text-orange-100">Scout opportunities and validate ideas with market research</p>
            </div>
            <div>
              <h3 className="text-6xl font-bold mb-2">02</h3>
              <h4 className="text-xl font-semibold mb-2">Build</h4>
              <p className="text-orange-100">Develop MVPs and scale with our technical team</p>
            </div>
            <div>
              <h3 className="text-6xl font-bold mb-2">03</h3>
              <h4 className="text-xl font-semibold mb-2">Scale</h4>
              <p className="text-orange-100">Go to market and achieve sustainable growth</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
